import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getReport, MEDDPICC_LABELS, PERSONA_IMAGES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  TrendingUp, TrendingDown, Award, Target, MessageSquare, Lightbulb,
  ArrowRight, RotateCcw, CheckCircle2, AlertCircle, BookOpen,
  Users, FileText, User
} from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell
} from "recharts";

const PERSONA_META = {
  jamie_sterling: { name: "Jamie Sterling", role: "Managing Director", short: "JS", focus: ["M", "E"] },
  evelyn_reed: { name: "Dr. Evelyn Reed", role: "Operating Partner", short: "ER", focus: ["D1", "C1"] },
  ben_carter: { name: "Ben Carter", role: "VP Procurement", short: "BC", focus: ["P", "C2"] },
};

export const ReportPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  useEffect(() => {
    loadReport();
  }, [sessionId]);

  const loadReport = async () => {
    try {
      const response = await getReport(sessionId);
      setReport(response.data);
    } catch (error) {
      console.error("Failed to load report:", error);
      toast.error("Failed to load evaluation report");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-slate-400">Generating evaluation report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Card className="max-w-md border-slate-200">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
            <h3 className="text-base font-semibold mb-2">Report Not Available</h3>
            <p className="text-sm text-slate-400 mb-4">
              Unable to load the evaluation report. The simulation may still be in progress.
            </p>
            <Button onClick={() => navigate("/")} data-testid="back-home-btn" className="bg-primary">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Build chart data
  const radarData = Object.entries(report.meddpicc_scores || {}).map(([key, value]) => {
    const shortKey = key === "Metrics" ? "M" : key === "EconomicBuyer" ? "E" :
      key === "DecisionCriteria" ? "D1" : key === "DecisionProcess" ? "D2" :
      key === "PaperProcess" ? "P" : key === "IdentifyPain" ? "I" :
      key === "Champion" ? "C1" : key === "Competition" ? "C2" : key;
    return {
      dimension: shortKey,
      fullName: MEDDPICC_LABELS[shortKey]?.name || key,
      score: value,
      fullMark: 100,
      fill: MEDDPICC_LABELS[shortKey]?.color || "#64748B"
    };
  });

  const barData = Object.entries(report.meddpicc_scores || {}).map(([key, value]) => {
    const shortKey = key === "Metrics" ? "M" : key === "EconomicBuyer" ? "E" :
      key === "DecisionCriteria" ? "D1" : key === "DecisionProcess" ? "D2" :
      key === "PaperProcess" ? "P" : key === "IdentifyPain" ? "I" :
      key === "Champion" ? "C1" : key === "Competition" ? "C2" : key;
    return {
      name: shortKey,
      fullName: MEDDPICC_LABELS[shortKey]?.name || key,
      score: value,
      color: MEDDPICC_LABELS[shortKey]?.color || "#64748B"
    };
  });

  const dviPercent = Math.round((report.deal_velocity_index || 0) * 100);
  const transcript = report.transcript || [];
  const perPersona = report.per_persona_state || {};
  const allPersonas = [...(report.completed_personas || [])];
  const introduced = report.personas_introduced || [];
  // Add any persona in introduced but not completed
  introduced.forEach(p => { if (!allPersonas.includes(p)) allPersonas.push(p); });

  const getBandColor = (band) => {
    switch (band) {
      case "Mastery": return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Proficient": return "bg-blue-50 text-blue-700 border-blue-200";
      case "Developing": return "bg-amber-50 text-amber-700 border-amber-200";
      default: return "bg-red-50 text-red-700 border-red-200";
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Transcript Sheet */}
      <Sheet open={transcriptOpen} onOpenChange={setTranscriptOpen}>
        <SheetContent side="right" className="w-[450px] sm:w-[550px]" aria-describedby="transcript-desc">
          <SheetHeader>
            <SheetTitle className="text-primary">Full Transcript</SheetTitle>
            <p id="transcript-desc" className="text-xs text-slate-400">Complete conversation history.</p>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-80px)] mt-4 pr-4">
            <div className="space-y-3">
              {transcript.map((msg, idx) => {
                if (msg.role === "system") {
                  return (
                    <div key={idx} className="text-center py-2">
                      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-400">
                        {msg.content}
                      </Badge>
                    </div>
                  );
                }
                const isUser = msg.role === "user";
                const meta = PERSONA_META[msg.persona_id];
                return (
                  <div key={idx} className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
                    <Avatar className="w-7 h-7 flex-shrink-0 mt-0.5">
                      {isUser ? (
                        <AvatarFallback className="bg-primary text-white text-[10px]"><User className="w-3 h-3" /></AvatarFallback>
                      ) : (
                        <>
                          <AvatarImage src={PERSONA_IMAGES[msg.persona_id]} />
                          <AvatarFallback className="bg-slate-100 text-[10px]">{meta?.short || "AI"}</AvatarFallback>
                        </>
                      )}
                    </Avatar>
                    <div className={`max-w-[80%] ${isUser ? "text-right" : ""}`}>
                      {!isUser && (
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[10px] font-medium text-slate-400">{msg.persona_name || meta?.name}</span>
                          {msg.trust_delta !== undefined && msg.trust_delta !== 0 && (
                            <Badge variant="outline" className={`text-[9px] py-0 h-3.5 ${
                              msg.trust_delta > 0 ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"
                            }`}>
                              {msg.trust_delta > 0 ? <TrendingUp className="w-2 h-2 mr-0.5" /> : <TrendingDown className="w-2 h-2 mr-0.5" />}
                              {msg.trust_delta > 0 ? "+" : ""}{msg.trust_delta}
                            </Badge>
                          )}
                        </div>
                      )}
                      <div className={`rounded-xl px-3 py-2 text-sm inline-block text-left ${
                        isUser ? "bg-primary text-white" : "bg-slate-50 text-slate-700 border border-slate-200"
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Header */}
      <header className="bg-white border-b border-slate-100 accent-border-top">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Deal Velocity</h1>
                <p className="text-xs text-slate-400">Evaluation Report</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTranscriptOpen(true)}
                className="gap-1.5 border-primary/30 text-primary"
                data-testid="open-transcript-btn"
              >
                <FileText className="w-4 h-4" />
                Full Transcript
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} data-testid="new-simulation-btn" className="border-slate-200 text-slate-600">
                <RotateCcw className="w-4 h-4 mr-2" />
                New Simulation
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Executive Summary */}
        <Card className="mb-8 border-slate-200">
          <CardContent className="pt-6">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="relative w-36 h-36 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="72" cy="72" r="64" stroke="#F1F5F9" strokeWidth="10" fill="none" />
                    <circle
                      cx="72" cy="72" r="64"
                      stroke={dviPercent >= 70 ? "#10B981" : dviPercent >= 55 ? "#F59E0B" : "#EF4444"}
                      strokeWidth="10" fill="none"
                      strokeDasharray={`${dviPercent * 4.02} 402`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-slate-900" data-testid="dvi-score">{dviPercent}</span>
                    <span className="text-xs text-slate-400">DVI Score</span>
                  </div>
                </div>
                <Badge className={`${getBandColor(report.performance_band)} text-sm px-3 py-1`} data-testid="performance-band">
                  {report.performance_band}
                </Badge>
              </div>
              <div className="lg:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-5 h-5 text-primary" />
                  <h2 className="text-base font-bold text-primary">Executive Summary</h2>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed" data-testid="executive-summary">
                  {report.executive_summary}
                </p>
                {allPersonas.length > 1 && (
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-400">Stakeholders engaged:</span>
                    {allPersonas.map(pid => (
                      <div key={pid} className="flex items-center gap-1.5">
                        <Avatar className="w-5 h-5">
                          <AvatarImage src={PERSONA_IMAGES[pid]} />
                          <AvatarFallback className="text-[8px] bg-slate-100">{PERSONA_META[pid]?.short}</AvatarFallback>
                        </Avatar>
                        <span className="text-xs text-slate-600">{PERSONA_META[pid]?.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="scores" className="space-y-6">
          <TabsList className="bg-slate-50 border border-slate-200 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="scores" className="gap-1.5 data-[state=active]:bg-white data-[state=active]:text-primary">
              <Target className="w-3.5 h-3.5" />MEDDPICC
            </TabsTrigger>
            {allPersonas.length > 0 && (
              <TabsTrigger value="stakeholders" className="gap-1.5 data-[state=active]:bg-white data-[state=active]:text-primary">
                <Users className="w-3.5 h-3.5" />Stakeholders
              </TabsTrigger>
            )}
            <TabsTrigger value="feedback" className="gap-1.5 data-[state=active]:bg-white data-[state=active]:text-primary">
              <MessageSquare className="w-3.5 h-3.5" />Feedback
            </TabsTrigger>
            <TabsTrigger value="coaching" className="gap-1.5 data-[state=active]:bg-white data-[state=active]:text-primary">
              <Lightbulb className="w-3.5 h-3.5" />Coaching
            </TabsTrigger>
            <TabsTrigger value="actions" className="gap-1.5 data-[state=active]:bg-white data-[state=active]:text-primary">
              <BookOpen className="w-3.5 h-3.5" />Action Plan
            </TabsTrigger>
          </TabsList>

          {/* MEDDPICC Scores Tab */}
          <TabsContent value="scores">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-sm text-primary">MEDDPICC Radar</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#E2E8F0" />
                        <PolarAngleAxis 
                          dataKey="dimension" 
                          tick={({ payload, x, y, textAnchor }) => {
                            const item = radarData.find(d => d.dimension === payload.value);
                            return (
                              <text x={x} y={y} textAnchor={textAnchor} fill={item?.fill || "#64748B"} fontSize={12} fontWeight={600}>
                                {payload.value}
                              </text>
                            );
                          }}
                        />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: "#CBD5E1" }} />
                        <Radar name="Score" dataKey="score" stroke="#2563EB" fill="#2563EB" fillOpacity={0.1} strokeWidth={2} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-sm text-primary">Dimension Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={barData} layout="vertical">
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 9, fill: "#94A3B8" }} />
                        <YAxis type="category" dataKey="name" width={30}
                          tick={({ payload, x, y }) => {
                            const item = barData.find(d => d.name === payload.value);
                            return <text x={x} y={y} textAnchor="end" fill={item?.color || "#64748B"} fontSize={11} fontWeight={600} dy={4}>{payload.value}</text>;
                          }}
                        />
                        <Tooltip formatter={(value, name, props) => [value, props.payload.fullName]}
                          contentStyle={{ backgroundColor: "white", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "12px" }}
                        />
                        <Bar dataKey="score" radius={[0, 3, 3, 0]}>
                          {barData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-6 border-slate-200">
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-wrap gap-4 justify-center">
                  {barData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-slate-500">
                        <span className="font-semibold text-slate-700">{item.name}</span>: {item.fullName} ({item.score})
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Per-Stakeholder Tab */}
          {allPersonas.length > 0 && (
            <TabsContent value="stakeholders">
              <div className="space-y-6">
                {allPersonas.map((pid) => {
                  const meta = PERSONA_META[pid];
                  const ps = perPersona[pid];
                  if (!meta) return null;
                  const personaMessages = transcript.filter(m => 
                    m.persona_id === pid || (m.role === "user" && transcript.some((pm, idx) => {
                      const nextPersonaMsg = transcript.slice(idx + 1).find(nm => nm.role === "persona");
                      return nextPersonaMsg?.persona_id === pid;
                    }))
                  );
                  const personaTrustDeltas = transcript.filter(m => m.persona_id === pid && m.trust_delta).map(m => m.trust_delta);
                  const totalDelta = personaTrustDeltas.reduce((sum, d) => sum + d, 0);
                  
                  return (
                    <Card key={pid} className="border-slate-200">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10 border-2 border-slate-100">
                            <AvatarImage src={PERSONA_IMAGES[pid]} />
                            <AvatarFallback className="bg-slate-100">{meta.short}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <CardTitle className="text-sm">{meta.name}</CardTitle>
                            <CardDescription className="text-xs">{meta.role}</CardDescription>
                          </div>
                          <div className="flex items-center gap-3 text-right">
                            {ps && (
                              <>
                                <div>
                                  <p className="text-lg font-bold text-slate-900">{ps.trust_at_end}</p>
                                  <p className="text-[10px] text-slate-400">Final Trust</p>
                                </div>
                                <div>
                                  <p className={`text-lg font-bold ${totalDelta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                                    {totalDelta >= 0 ? "+" : ""}{totalDelta}
                                  </p>
                                  <p className="text-[10px] text-slate-400">Trust Change</p>
                                </div>
                                <div>
                                  <p className="text-lg font-bold text-slate-900">{ps.turns}</p>
                                  <p className="text-[10px] text-slate-400">Turns</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {/* MEDDPICC focus areas and progress for this persona */}
                        <div className="flex items-center gap-2 mb-3">
                          <Target className="w-3.5 h-3.5 text-primary" />
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Focus Areas</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {meta.focus.map(dim => {
                            const progress = ps?.meddpicc_snapshot?.[dim];
                            const percent = progress ? Math.round(progress * 100) : 0;
                            const info = MEDDPICC_LABELS[dim];
                            return (
                              <div key={dim} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: info?.color || "#64748B" }} />
                                <span className="text-xs font-semibold" style={{ color: info?.color }}>{dim}</span>
                                <span className="text-xs text-slate-400">{info?.name}</span>
                                <Badge variant="outline" className="text-[10px] py-0 h-4 ml-1 bg-white">{percent}%</Badge>
                              </div>
                            );
                          })}
                        </div>

                        {/* Full MEDDPICC snapshot if available */}
                        {ps?.meddpicc_snapshot && (
                          <div className="grid grid-cols-8 gap-2">
                            {Object.entries(ps.meddpicc_snapshot).map(([dim, val]) => {
                              const percent = Math.round(val * 100);
                              const info = MEDDPICC_LABELS[dim];
                              const isFocus = meta.focus.includes(dim);
                              return (
                                <div key={dim} className={`text-center p-2 rounded-lg ${isFocus ? "bg-primary/5 border border-primary/20" : "bg-slate-50 border border-slate-100"}`}>
                                  <p className="text-[10px] font-bold" style={{ color: info?.color }}>{dim}</p>
                                  <p className={`text-sm font-semibold ${percent >= 50 ? "text-slate-900" : "text-slate-400"}`}>{percent}%</p>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Trust delta trajectory */}
                        {personaTrustDeltas.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-slate-100">
                            <p className="text-xs text-slate-400 mb-2">Trust trajectory per turn:</p>
                            <div className="flex items-center gap-1 flex-wrap">
                              {personaTrustDeltas.map((delta, i) => (
                                <Badge key={i} variant="outline" className={`text-[10px] py-0 h-5 ${
                                  delta > 0 ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                                  delta < 0 ? "bg-red-50 text-red-600 border-red-200" :
                                  "bg-slate-50 text-slate-400 border-slate-200"
                                }`}>
                                  {delta > 0 ? "+" : ""}{delta}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          )}

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <div className="grid md:grid-cols-2 gap-6">
              {report.qualitative_feedback && Object.entries(report.qualitative_feedback).map(([key, feedback]) => (
                <Card key={key} className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-primary capitalize">{key.replace(/_/g, " ")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 leading-relaxed">{feedback}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Coaching Tab */}
          <TabsContent value="coaching">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-sm text-primary">Coaching Highlights</CardTitle>
              </CardHeader>
              <CardContent>
                {report.coach_integration_highlights?.length > 0 ? (
                  <ul className="space-y-3">
                    {report.coach_integration_highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-600">{h}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No coaching highlights recorded.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Action Plan Tab */}
          <TabsContent value="actions">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-sm text-primary">Recommended Action Plan</CardTitle>
              </CardHeader>
              <CardContent>
                {report.action_plan_recommendations?.length > 0 ? (
                  <div className="space-y-4">
                    {report.action_plan_recommendations.map((rec, i) => (
                      <div key={i} className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                        <div className="flex items-start gap-3">
                          <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">
                            {i + 1}
                          </div>
                          <div>
                            <h4 className="font-semibold text-sm text-slate-800 mb-1">{rec.area}</h4>
                            <p className="text-sm text-slate-500">{rec.scenario_recommendation}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No specific recommendations generated.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <div className="mt-10 text-center">
          <Card className="max-w-lg mx-auto border-slate-200">
            <CardContent className="pt-6 pb-6">
              <h3 className="text-base font-bold text-slate-900 mb-2">Ready for Another Round?</h3>
              <p className="text-sm text-slate-400 mb-4">Practice makes perfect. Start a new simulation to improve.</p>
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setTranscriptOpen(true)}
                  className="border-primary/30 text-primary"
                  data-testid="review-transcript-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Review Transcript
                </Button>
                <Button 
                  size="lg" 
                  onClick={() => navigate("/")}
                  className="font-semibold bg-primary hover:bg-primary/90"
                  data-testid="start-new-simulation-btn"
                >
                  Start New Simulation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
